import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CopyrightIcon from '@mui/icons-material/Copyright';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import DryCleaningIcon from '@mui/icons-material/DryCleaning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import LayersIcon from '@mui/icons-material/Layers';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import PersonIcon from '@mui/icons-material/Person';
import PublicIcon from '@mui/icons-material/Public';
import SchoolIcon from '@mui/icons-material/School';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import {
  Alert,
  Box,
  ButtonBase,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import {
  TaxonomyCategoryNode,
  TaxonomyLayerNode,
  TaxonomyTreeResponse,
  taxonomyTreeService,
} from '../services/taxonomyTreeService';

const LAYER_VISUALS: Record<string, { icon: React.ElementType; color: string }> = {
  G: { icon: MusicNoteIcon, color: '#1976d2' },
  S: { icon: StarIcon, color: '#e91e63' },
  L: { icon: DryCleaningIcon, color: '#9c27b0' },
  M: { icon: DirectionsRunIcon, color: '#ff9800' },
  W: { icon: PublicIcon, color: '#4caf50' },
  B: { icon: LocalOfferIcon, color: '#f44336' },
  P: { icon: PersonIcon, color: '#00bcd4' },
  T: { icon: SchoolIcon, color: '#673ab7' },
  C: { icon: LayersIcon, color: '#795548' },
  R: { icon: CopyrightIcon, color: '#607d8b' },
};

const DEFAULT_LAYER_VISUAL = {
  icon: LayersIcon,
  color: '#1976d2',
};

const TaxonomyPage: React.FC = () => {
  const [taxonomy, setTaxonomy] = useState<TaxonomyTreeResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [expandedLayers, setExpandedLayers] = useState<Record<string, boolean>>({});
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [copiedSubcategoryKey, setCopiedSubcategoryKey] = useState<string | null>(null);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState<boolean>(false);
  const [copiedValue, setCopiedValue] = useState<string>('');

  const taxonomyStats = useMemo(() => {
    const layers = taxonomy?.layers || [];
    const categories = layers.reduce(
      (total, layer) => total + (layer.categories?.length || 0),
      0
    );
    const subcategories = layers.reduce(
      (total, layer) =>
        total +
        (layer.categories || []).reduce(
          (categoryTotal, category) =>
            categoryTotal + (category.subcategories?.length || 0),
          0
        ),
      0
    );

    return {
      layers: layers.length,
      categories,
      subcategories,
    };
  }, [taxonomy]);

  const filteredLayerNodes = useMemo<TaxonomyLayerNode[]>(() => {
    if (!taxonomy) {
      return [];
    }

    const trimmed = searchQuery.trim().toLowerCase();
    if (!trimmed) {
      return taxonomy.layers;
    }

    return taxonomy.layers
      .map(layer => {
        const layerMatches =
          layer.code.toLowerCase().includes(trimmed) ||
          layer.name.toLowerCase().includes(trimmed);

        const categories = (layer.categories || [])
          .map(category => {
            const categoryMatches =
              category.code.toLowerCase().includes(trimmed) ||
              category.name.toLowerCase().includes(trimmed);

            const matchingSubcategories = (category.subcategories || []).filter(
              subcategory =>
                subcategory.code.toLowerCase().includes(trimmed) ||
                subcategory.name.toLowerCase().includes(trimmed)
            );

            if (layerMatches || categoryMatches) {
              return category;
            }

            if (matchingSubcategories.length > 0) {
              return {
                ...category,
                subcategories: matchingSubcategories,
              };
            }

            return null;
          })
          .filter((category): category is TaxonomyCategoryNode => category !== null);

        if (layerMatches) {
          return layer;
        }

        if (categories.length > 0) {
          return {
            ...layer,
            categories,
          };
        }

        return null;
      })
      .filter((layer): layer is TaxonomyLayerNode => layer !== null);
  }, [taxonomy, searchQuery]);

  const isSearchActive = searchQuery.trim().length > 0;

  const toggleLayer = (layerCode: string) => {
    setExpandedLayers(previous => ({
      ...previous,
      [layerCode]: !previous[layerCode],
    }));
  };

  const toggleCategory = (categoryKey: string) => {
    setExpandedCategories(previous => ({
      ...previous,
      [categoryKey]: !previous[categoryKey],
    }));
  };

  const expandAll = () => {
    const nextExpandedLayers: Record<string, boolean> = {};
    const nextExpandedCategories: Record<string, boolean> = {};

    (taxonomy?.layers || []).forEach(layer => {
      nextExpandedLayers[layer.code] = true;
      (layer.categories || []).forEach(category => {
        nextExpandedCategories[`${layer.code}:${category.code}`] = true;
      });
    });

    setExpandedLayers(nextExpandedLayers);
    setExpandedCategories(nextExpandedCategories);
  };

  const collapseAll = () => {
    setExpandedLayers({});
    setExpandedCategories({});
  };

  const loadTaxonomy = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const tree = await taxonomyTreeService.loadTree();
      setTaxonomy(tree);
    } catch (fetchError) {
      setError(
        fetchError instanceof Error
          ? fetchError.message
          : 'Failed to load taxonomy tree.'
      );
      setTaxonomy(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTaxonomy();
  }, []);

  const copyToClipboard = async (value: string) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  };

  const handleSubcategoryClick = async (
    layerCode: string,
    categoryCode: string,
    subcategoryCode: string
  ) => {
    const value = `${layerCode}.${categoryCode}.${subcategoryCode}`;
    const rowKey = `${layerCode}:${categoryCode}:${subcategoryCode}`;

    try {
      await copyToClipboard(value);
      setCopiedSubcategoryKey(rowKey);
      setCopiedValue(value);
      setCopySnackbarOpen(true);
    } catch (clipboardError) {
      console.error('Failed to copy taxonomy code:', clipboardError);
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper
          elevation={2}
          sx={{
            p: 4,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            minHeight: 240,
          }}
        >
          <CircularProgress size={28} />
          <Typography variant="body1">Loading taxonomy tree...</Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                color: '#1976d2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#1976d215',
                width: 48,
                height: 48,
                borderRadius: '50%',
              }}
            >
              <AccountTreeIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Taxonomy
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Browse all layers, categories, and subcategories used in registry assets.
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField
              fullWidth
              label="Search taxonomy"
              value={searchQuery}
              onChange={event => setSearchQuery(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Chip size="small" label={`${taxonomyStats.layers} layers`} />
              <Chip size="small" label={`${taxonomyStats.categories} categories`} />
              <Chip size="small" label={`${taxonomyStats.subcategories} subcategories`} />
              {isSearchActive && (
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={`${filteredLayerNodes.length} matching layers`}
                />
              )}
              <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                <Button size="small" variant="outlined" onClick={expandAll}>
                  Expand all
                </Button>
                <Button size="small" variant="outlined" onClick={collapseAll}>
                  Collapse all
                </Button>
              </Box>
            </Box>
          </Box>
        </Paper>

        {error && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={loadTaxonomy}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {!error && filteredLayerNodes.length === 0 && (
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No taxonomy entries match your search.
            </Typography>
          </Paper>
        )}

        {!error &&
          filteredLayerNodes.map(layer => {
            const layerVisual = LAYER_VISUALS[layer.code] || DEFAULT_LAYER_VISUAL;
            const LayerIcon = layerVisual.icon;
            const isLayerExpanded = isSearchActive || expandedLayers[layer.code] === true;
            const layerHasChildren = (layer.categories || []).length > 0;

            return (
              <Paper
                key={layer.code}
                elevation={1}
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  borderLeft: '4px solid',
                  borderLeftColor: layerVisual.color,
                }}
              >
                <Box
                  onClick={() => {
                    if (layerHasChildren) {
                      toggleLayer(layer.code);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': { backgroundColor: 'action.hover' },
                    cursor: layerHasChildren ? 'pointer' : 'default',
                  }}
                >
                  <IconButton
                    size="small"
                    aria-label={`${
                      isLayerExpanded ? 'Collapse' : 'Expand'
                    } layer ${layer.name} (${layer.code})`}
                    onClick={event => {
                      event.stopPropagation();
                      toggleLayer(layer.code);
                    }}
                    disabled={!layerHasChildren}
                  >
                    {isLayerExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                  </IconButton>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 34,
                      height: 34,
                      borderRadius: '50%',
                      color: layerVisual.color,
                      backgroundColor: `${layerVisual.color}18`,
                    }}
                  >
                    <LayerIcon fontSize="small" />
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700 }}>
                      {`${layer.name} (${layer.code})`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(layer.categories || []).length} categories
                    </Typography>
                  </Box>

                  <Chip size="small" variant="outlined" label="Layer" sx={{ ml: 'auto' }} />
                </Box>

                <Collapse in={isLayerExpanded}>
                  <Box sx={{ pl: 4.5, pr: 1, pb: 0.5, pt: 0.5 }}>
                    {(layer.categories || []).map(category => {
                      const categoryKey = `${layer.code}:${category.code}`;
                      const isCategoryExpanded =
                        isSearchActive || expandedCategories[categoryKey] === true;
                      const categoryHasChildren =
                        (category.subcategories || []).length > 0;

                      return (
                        <Paper
                          key={categoryKey}
                          variant="outlined"
                          sx={{ p: 1, mt: 1, borderRadius: 2, borderColor: 'divider' }}
                        >
                          <Box
                            onClick={() => {
                              if (categoryHasChildren) {
                                toggleCategory(categoryKey);
                              }
                            }}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              p: 0.5,
                              borderRadius: 1.5,
                              '&:hover': { backgroundColor: 'action.hover' },
                              cursor: categoryHasChildren ? 'pointer' : 'default',
                            }}
                          >
                            <IconButton
                              size="small"
                              aria-label={`${
                                isCategoryExpanded ? 'Collapse' : 'Expand'
                              } category ${category.name} (${category.code})`}
                              onClick={event => {
                                event.stopPropagation();
                                toggleCategory(categoryKey);
                              }}
                              disabled={!categoryHasChildren}
                            >
                              {isCategoryExpanded ? (
                                <ExpandMoreIcon />
                              ) : (
                                <ChevronRightIcon />
                              )}
                            </IconButton>

                            <LabelOutlinedIcon fontSize="small" color="action" />

                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {`${category.name} (${category.code})`}
                            </Typography>

                            <Chip
                              size="small"
                              variant="outlined"
                              label={`${(category.subcategories || []).length} subcategories`}
                              sx={{ ml: 'auto' }}
                            />
                          </Box>

                          <Collapse in={isCategoryExpanded}>
                            <Box sx={{ pl: 4, pr: 0.5, py: 0.5 }}>
                              {(category.subcategories || []).map(subcategory => {
                                const subcategoryKey = `${layer.code}:${category.code}:${subcategory.code}`;
                                const isCopied = copiedSubcategoryKey === subcategoryKey;
                                const taxonomyCode = `${layer.code}.${category.code}.${subcategory.code}`;

                                return (
                                  <ButtonBase
                                    key={subcategoryKey}
                                    onClick={() =>
                                      handleSubcategoryClick(
                                        layer.code,
                                        category.code,
                                        subcategory.code
                                      )
                                    }
                                    aria-label={`Copy taxonomy code ${taxonomyCode}`}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      width: '100%',
                                      gap: 1,
                                      py: 0.75,
                                      px: 1,
                                      borderRadius: 1,
                                      cursor: 'copy',
                                      textAlign: 'left',
                                      '&:hover': { backgroundColor: 'action.hover' },
                                      '&:focus-visible': {
                                        outline: '2px solid',
                                        outlineColor: 'primary.main',
                                        outlineOffset: '1px',
                                      },
                                    }}
                                  >
                                    <Typography variant="body2">{subcategory.name}</Typography>
                                    <Chip
                                      size="small"
                                      icon={isCopied ? <CheckIcon /> : <ContentCopyIcon />}
                                      color={isCopied ? 'success' : 'default'}
                                      variant={isCopied ? 'filled' : 'outlined'}
                                      label={isCopied ? 'Copied' : subcategory.code}
                                    />
                                  </ButtonBase>
                                );
                              })}
                            </Box>
                          </Collapse>
                        </Paper>
                      );
                    })}
                  </Box>
                </Collapse>
              </Paper>
            );
          })}

        <Snackbar
          open={copySnackbarOpen}
          autoHideDuration={1600}
          onClose={() => setCopySnackbarOpen(false)}
          message={`Copied: ${copiedValue}`}
        />
      </Box>
    </Container>
  );
};

export default TaxonomyPage;
